import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel,
    TextField,
    Box,
    CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { postOnboardDeviceLogs } from '../api/postOnboardDeviceLogs';
import { LogStatus } from '../interfaces';
import { getOnboardDeviceLogsUploadStatus } from '../api/getOnboardDeviceLogsUploadStatus';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Divider from '@mui/material/Divider';
import { CopyTextCell } from './GridCells/CopyTextCell';

const columns: GridColDef[] = [
    { field: 'name', headerName: 'Log', width: 350, renderCell: CopyTextCell },
    { field: 'type', headerName: 'Log type' },
    { field: 'status', headerName: 'Status' },
    { field: 'totalFilesCount', headerName: 'Total files', type: 'number' },
    { field: 'uploadedFilesCount', headerName: 'Uploaded', type: 'number' },
    { field: 'failedFilesCount', headerName: 'Failed', type: 'number' },
    { field: 'details', headerName: 'Details', width: 350 },
];

interface Props {
    open: boolean;
    selectedDeviceId: string;
    onClose: () => void;
}

export const UploadOnboardDeviceLogsDialog = ({ open, onClose, selectedDeviceId }: Props) => {
    const [rows, setRows] = useState<LogStatus[]>([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(7, 'day'));
    const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
    const [allLogs, setAllLogs] = useState(false);
    const STORAGE_KEY = `logs-upload-request-id-device-${selectedDeviceId}`;
    const [requestId, setRequestId] = useState<string | null>(localStorage.getItem(STORAGE_KEY));
    const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const [uploadStatus, setUploadStatus] = useState<string | null>(null);
    const [uploadDetails, setUploadDetails] = useState<string | null>(null);
    const [uploadInProgress, setUploadInProgress] = useState<boolean>(requestId !== null);

    const fetchData = useCallback(async () => {
        if (!requestId) {
            return;
        }

        try {
            setLoading(true);
            const response = await getOnboardDeviceLogsUploadStatus(requestId);

            if (response.logsStatuses) {
                setRows(response.logsStatuses);
            }

            if (response.status) {
                setUploadStatus(response.status);
            }

            if (response.details) {
                setUploadDetails(response.details);
            }

            if (response.status !== 'in progress') {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }

                setUploadInProgress(false);
                setRequestId(null);
            }
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
        }
    }, [requestId, intervalId]);

    const startPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        fetchData(); // immediate call
        intervalRef.current = setInterval(fetchData, 5000);
    }, [fetchData]);

    useEffect(() => {
        if (requestId && open && !intervalRef.current) {
            startPolling();
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [requestId, open, startPolling]);

    const handleUpload = async () => {
        setUploadStatus(null);
        setUploadDetails(null);
        setRows([]);

        setUploadInProgress(true);

        try {
            const payload = allLogs
                ? {
                      startDate: null,
                      endDate: null,
                  }
                : {
                      startDate: startDate?.format('YYYY-MM-DD'),
                      endDate: endDate?.format('YYYY-MM-DD'),
                  };

            const response = await postOnboardDeviceLogs(selectedDeviceId, payload);
            const newRequestId = response.requestId;

            setRequestId(newRequestId);
            localStorage.setItem(STORAGE_KEY, newRequestId);

            startPolling();
        } catch (error: any) {
            console.error('Upload error:', error);
            setUploadStatus('failed');
            setUploadDetails('Failed to start upload');
            setUploadInProgress(false);
        }
    };

    const handleClose = () => {
        if (intervalId) {
            clearInterval(intervalId);
        }

        setIntervalId(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Upload Logs for Device {selectedDeviceId}</DialogTitle>
            <DialogContent>
                <Box display="flex" gap={2} alignItems="center" my={2} flexWrap="wrap">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                            label="Start Date"
                            value={startDate}
                            inputFormat="DD.MM.YYYY"
                            onChange={(newValue) => setStartDate(newValue)}
                            renderInput={(params) => <TextField {...params} />}
                            disabled={allLogs}
                        />
                        <DesktopDatePicker
                            label="End Date"
                            value={endDate}
                            inputFormat="DD.MM.YYYY"
                            onChange={(newValue) => setEndDate(newValue)}
                            renderInput={(params) => <TextField {...params} />}
                            disabled={allLogs}
                        />
                    </LocalizationProvider>
                    <Divider orientation="vertical" flexItem>
                        <p>or</p>
                    </Divider>
                    <FormControlLabel
                        control={
                            <Checkbox checked={allLogs} onChange={(e) => setAllLogs(e.target.checked)} />
                        }
                        label="All new logs"
                    />
                    <Button variant="contained" onClick={handleUpload} disabled={uploadInProgress}>
                        Upload
                        {uploadInProgress && <CircularProgress style={{ marginLeft: '5px' }} size={15} />}
                    </Button>
                </Box>
                {uploadStatus && (
                    <Box display="flex" gap={4} alignItems="center" my={2}>
                        <Box display="flex" gap={1}>
                            <strong>Status:</strong>
                            <p
                                style={{
                                    color:
                                        uploadStatus === 'failed'
                                            ? 'red'
                                            : uploadStatus === 'completed'
                                            ? 'green'
                                            : 'black',
                                    fontWeight: 'bold',
                                }}
                            >
                                {uploadStatus}
                            </p>
                        </Box>
                        {uploadDetails && (
                            <Box>
                                <strong>Details:</strong> {uploadDetails}
                            </Box>
                        )}
                    </Box>
                )}
                {rows.length > 0 && (
                    <Box height={400}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            loading={loading}
                            getRowId={(row) => `${row.type}-${row.name}`}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};
